/**
 * useAdmin Hook
 * 
 * Handles admin data fetching (statistics).
 * Encapsulates API calls per Rule 6.
 */

import { useState, useEffect, useCallback } from 'react';
import { authApi } from '../shared/utils/api';
import { logger } from '../shared/utils/logger';

// Types matching actual API response
export interface AdminUserStats {
  total_users: number;
  admin_count: number;
  user_count: number;
  verified_users: number;
  new_users_30d: number;
  active_users_30d: number;
  never_logged_in: number;
}

export interface AdminRequestStats {
  total_requests: number;
  pending_requests: number;
  approved_requests: number;
  denied_requests: number;
  new_requests_30d: number;
}

export interface AdminPortfolioStats {
  total_portfolios: number;
  new_portfolios_30d: number;
}

export interface AdminPropertyStats {
  total_properties: number;
  research_properties: number;
  under_contract_properties: number;
  owned_properties: number;
  listed_properties: number;
  sold_properties: number;
  new_properties_30d: number;
  total_property_value: number;
}

export interface AdminFinancialStats {
  total_entries: number;
  income_entries: number;
  expense_entries: number;
  total_income: number;
  total_expenses: number;
  new_entries_30d: number;
}

export interface UserGrowthPoint {
  month: string;
  count: number;
}

export interface AdminStats {
  users: AdminUserStats;
  accountRequests: AdminRequestStats;
  portfolios: AdminPortfolioStats;
  properties: AdminPropertyStats;
  financial: AdminFinancialStats;
  userGrowth: UserGrowthPoint[];
}

interface UseAdminReturn {
  stats: AdminStats | null;
  pendingRequests: number;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

function parseNumber(val: unknown): number {
  if (typeof val === 'number') return val;
  if (typeof val === 'string') return parseInt(val, 10) || 0;
  return 0;
}

function transformStats(raw: any): AdminStats {
  return {
    users: {
      total_users: parseNumber(raw?.users?.total_users),
      admin_count: parseNumber(raw?.users?.admin_count),
      user_count: parseNumber(raw?.users?.user_count),
      verified_users: parseNumber(raw?.users?.verified_users),
      new_users_30d: parseNumber(raw?.users?.new_users_30d),
      active_users_30d: parseNumber(raw?.users?.active_users_30d),
      never_logged_in: parseNumber(raw?.users?.never_logged_in),
    },
    accountRequests: {
      total_requests: parseNumber(raw?.accountRequests?.total_requests),
      pending_requests: parseNumber(raw?.accountRequests?.pending_requests),
      approved_requests: parseNumber(raw?.accountRequests?.approved_requests),
      denied_requests: parseNumber(raw?.accountRequests?.denied_requests),
      new_requests_30d: parseNumber(raw?.accountRequests?.new_requests_30d),
    },
    portfolios: {
      total_portfolios: parseNumber(raw?.portfolios?.total_portfolios),
      new_portfolios_30d: parseNumber(raw?.portfolios?.new_portfolios_30d),
    },
    properties: {
      total_properties: parseNumber(raw?.properties?.total_properties),
      research_properties: parseNumber(raw?.properties?.research_properties),
      under_contract_properties: parseNumber(raw?.properties?.under_contract_properties),
      owned_properties: parseNumber(raw?.properties?.owned_properties),
      listed_properties: parseNumber(raw?.properties?.listed_properties),
      sold_properties: parseNumber(raw?.properties?.sold_properties),
      new_properties_30d: parseNumber(raw?.properties?.new_properties_30d),
      total_property_value: parseNumber(raw?.properties?.total_property_value),
    },
    financial: {
      total_entries: parseNumber(raw?.financial?.total_entries),
      income_entries: parseNumber(raw?.financial?.income_entries),
      expense_entries: parseNumber(raw?.financial?.expense_entries),
      total_income: parseNumber(raw?.financial?.total_income),
      total_expenses: parseNumber(raw?.financial?.total_expenses),
      new_entries_30d: parseNumber(raw?.financial?.new_entries_30d),
    },
    userGrowth: Array.isArray(raw?.userGrowth) ? raw.userGrowth : [],
  };
}

export function useAdmin(): UseAdminReturn {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await authApi.getAdminStatistics();
      if (response.success && response.statistics) {
        const transformed = transformStats(response.statistics);
        setStats(transformed);
      } else {
        setError('Failed to load statistics');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load admin data';
      logger.error('Admin stats fetch failed', 'admin', String(err));
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return {
    stats,
    pendingRequests: stats?.accountRequests?.pending_requests || 0,
    loading,
    error,
    refresh: fetchStats,
  };
}
